import React, { useEffect, useMemo, useRef, useState } from "react";
import { getProfile, updateProfile } from "../../services/profile.service";
import { updateAuthUser } from "../AdminAuth/components/authStore";
import { env } from "../../config/env";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";
const BASE_URL = env.AUTH_BASE_URL || "";

function toAbsoluteFileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BASE_URL}${path}`;
}

function getBestProfilePhoto(user = {}) {
  return (
    user?.onboarding?.documents?.profilePhoto?.url ||
    user?.onboarding?.photos?.photo1?.url ||
    user?.profileImage ||
    user?.profile_image ||
    user?.avatarUrl ||
    user?.image ||
    user?.avatar ||
    ""
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[16px] bg-white shadow-sm ${className}`}
      style={{ border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ title, subtitle = "" }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-bold text-slate-800">{title}</h2>
      {subtitle ? (
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      ) : null}
    </div>
  );
}

function Field({ label, value, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 ${className}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 break-words text-sm font-medium text-slate-800">
        {value || "-"}
      </div>
    </div>
  );
}

function StatusPill({ value, type = "default" }) {
  const styles = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${styles[type] || styles.default}`}
    >
      {value || "-"}
    </span>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder = "",
  disabled = false,
}) {
  return (
    <label className="block">
      <div className="mb-1 text-[13px] font-semibold text-slate-600">{label}</div>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="h-11 w-full rounded-xl border border-slate-300 px-3 text-sm outline-none focus:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-100"
      />
    </label>
  );
}

function FileRow({
  label,
  file,
  existingUrl,
  existingName,
  onChange,
  editable = false,
}) {
  const href = existingUrl
    ? /^https?:\/\//i.test(existingUrl)
      ? existingUrl
      : `${BASE_URL}${existingUrl}`
    : "";

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="mb-2 text-sm font-semibold text-slate-700">{label}</div>

      {existingUrl ? (
        <div className="mb-3 text-sm">
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline"
          >
            {existingName || "View current file"}
          </a>
        </div>
      ) : (
        <div className="mb-3 text-sm text-slate-500">No file uploaded</div>
      )}

      {editable ? (
        <>
          <input
            type="file"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
          {file ? (
            <div className="mt-2 text-xs text-slate-500">Selected: {file.name}</div>
          ) : null}
        </>
      ) : (
        <div className="text-xs text-slate-400">Click Edit to change this file</div>
      )}
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

function normalizeProfileResponse(data) {
  if (!data) return null;

  const admin =
    data.admin && typeof data.admin === "object"
      ? data.admin
      : data.user && typeof data.user === "object"
      ? data.user
      : data;

  const dashboardAccess = data?.dashboardAccess ?? admin?.dashboardAccess ?? {};
  const warehouseAccess = data?.warehouseAccess ?? admin?.warehouseAccess ?? {};

  const selectedWarehouseId =
    data?.selectedWarehouseId ??
    admin?.selectedWarehouseId ??
    dashboardAccess?.warehouseScope?.selectedWarehouseId ??
    warehouseAccess?.selectedWarehouseId ??
    admin?.assignedWarehouseId ??
    dashboardAccess?.warehouseScope?.assignedWarehouseId ??
    warehouseAccess?.assignedWarehouseId ??
    null;

  const selectedWarehouseName =
    data?.selectedWarehouseName ??
    admin?.selectedWarehouseName ??
    dashboardAccess?.warehouseScope?.selectedWarehouseName ??
    warehouseAccess?.selectedWarehouseName ??
    dashboardAccess?.warehouseScope?.assignedWarehouseName ??
    warehouseAccess?.assignedWarehouseName ??
    dashboardAccess?.warehouseScope?.warehouses?.find((warehouse) => warehouse?.id === selectedWarehouseId)?.name ??
    warehouseAccess?.warehouses?.find((warehouse) => warehouse?.id === selectedWarehouseId)?.name ??
    null;

  return {
    ...admin,
    nextAction: data?.nextAction ?? admin?.nextAction ?? "",
    mustChangePassword:
      data?.mustChangePassword ?? admin?.mustChangePassword ?? false,
    isOnboardingComplete:
      data?.isOnboardingComplete ?? admin?.isOnboardingComplete ?? false,
    onboardingStep: data?.onboardingStep ?? admin?.onboardingStep ?? 0,
    approvalStatus: data?.approvalStatus ?? admin?.approvalStatus ?? "",
    isApproved: data?.isApproved ?? admin?.isApproved ?? false,
    approvalNote: data?.approvalNote ?? admin?.approvalNote ?? "",
    dashboardAccess,
    warehouseAccess,
    currentDashboard: data?.currentDashboard ?? admin?.currentDashboard ?? "",
    selectedWarehouseId,
    selectedWarehouseName,
  };
}

function getApprovalTone(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return "success";
  if (s === "PENDING") return "warning";
  if (s === "REJECTED") return "danger";
  return "default";
}

function getOnboardingTone(isComplete) {
  return isComplete ? "success" : "warning";
}

function buildFullAddress(address = {}) {
  const parts = [
    address?.line1,
    address?.city,
    address?.state,
    address?.country,
    address?.pincode,
  ].filter(Boolean);

  return parts.length ? parts.join(", ") : "-";
}

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [user, setUser] = useState(null);

  const [emergencyName, setEmergencyName] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [emergencyRelation, setEmergencyRelation] = useState("");

  const [referenceName, setReferenceName] = useState("");
  const [referencePhone, setReferencePhone] = useState("");
  const [referenceRelation, setReferenceRelation] = useState("");

  const profilePhotoInputRef = useRef(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [panFile, setPanFile] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (!profilePhotoFile) {
      setProfilePhotoPreview("");
      return undefined;
    }

    const nextPreviewUrl = URL.createObjectURL(profilePhotoFile);
    setProfilePhotoPreview(nextPreviewUrl);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [profilePhotoFile]);

  function fillForm(admin) {
    const ec = admin?.onboarding?.emergencyContact || {};
    const refs = admin?.onboarding?.referenceContacts || [];
    const firstRef = refs?.[0] || {};

    setEmergencyName(ec?.name || "");
    setEmergencyPhone(ec?.phone || "");
    setEmergencyRelation(ec?.relation || ec?.relationship || "");

    setReferenceName(firstRef?.name || "");
    setReferencePhone(firstRef?.phone || "");
    setReferenceRelation(firstRef?.relation || firstRef?.relationship || "");
  }

  async function loadProfile() {
    try {
      setLoading(true);
      const data = await getProfile();
      const admin = normalizeProfileResponse(data);
      setUser(admin);
      fillForm(admin);

      const latestProfilePhoto = getBestProfilePhoto(admin);
      updateAuthUser({
        ...admin,
        avatar: latestProfilePhoto || admin?.avatar || "",
      });
    } catch (err) {
      console.error("PROFILE LOAD ERROR =>", err);
      alert(err.message || "Failed to load profile");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  function handleEdit() {
    fillForm(user);
    setProfilePhotoFile(null);
    setAadhaarFile(null);
    setPanFile(null);
    setIsEditMode(true);
  }

  function handleCancel() {
    fillForm(user);
    setProfilePhotoFile(null);
    setAadhaarFile(null);
    setPanFile(null);
    setIsEditMode(false);
  }

  const avatarUrl = useMemo(() => {
    const raw = getBestProfilePhoto(user);
    return toAbsoluteFileUrl(raw);
  }, [user]);

  const visibleAvatarUrl = profilePhotoPreview || avatarUrl;

  function handleProfilePhotoPick(file) {
    if (!file) {
      setProfilePhotoFile(null);
      return;
    }

    if (!String(file.type || "").startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    setProfilePhotoFile(file);
  }

  function openProfilePhotoPicker() {
    if (!isEditMode || saving) return;
    profilePhotoInputRef.current?.click();
  }

  async function handleSave() {
    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("onboardingStep", String(user?.onboardingStep || 5));
      formData.append(
        "isOnboardingComplete",
        String(Boolean(user?.isOnboardingComplete))
      );

      formData.append(
        "emergencyContact",
        JSON.stringify({
          name: emergencyName,
          phone: emergencyPhone,
          relation: emergencyRelation,
        })
      );

      const refs = [];
      if (referenceName || referencePhone || referenceRelation) {
        refs.push({
          name: referenceName,
          phone: referencePhone,
          relation: referenceRelation,
        });
      }

      formData.append("referenceContacts", JSON.stringify(refs));
      formData.append(
        "excelKnowledgeConfirmed",
        String(Boolean(user?.onboarding?.excelKnowledgeConfirmed))
      );

      if (profilePhotoFile) {
        formData.append("documents.profilePhoto", profilePhotoFile);
      }
      if (aadhaarFile) {
        formData.append("documents.aadhaar", aadhaarFile);
      }
      if (panFile) {
        formData.append("documents.pan", panFile);
      }

      await updateProfile(formData);
      await loadProfile();

      setProfilePhotoFile(null);
      setAadhaarFile(null);
      setPanFile(null);
      setIsEditMode(false);

      alert("Profile updated successfully");
    } catch (err) {
      console.error("PROFILE UPDATE ERROR =>", err);
      alert(err.message || "Profile update failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading profile...</div>;
  }

  if (!user) {
    return <div className="p-6 text-sm text-rose-600">Profile not found.</div>;
  }

  const docs = user?.onboarding?.documents || {};
  const onboardingAddress = user?.onboarding?.address || {};
  const fullAddress = buildFullAddress(onboardingAddress);

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-8xl space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">My Profile</h1>
            <p className="mt-1 text-sm text-slate-500">
              View your profile details, onboarding status, and uploaded documents.
            </p>
          </div>

          {!isEditMode ? (
            <button
              type="button"
              onClick={handleEdit}
              className="h-11 rounded-xl px-5 text-sm font-semibold text-white transition"
              style={{ backgroundColor: PINK }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = PINK_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = PINK;
              }}
            >
              Edit Profile
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="h-11 rounded-xl border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="h-11 rounded-xl px-5 text-sm font-semibold text-white transition"
                style={{ backgroundColor: saving ? "#f3aab3" : PINK }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = PINK_HOVER;
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = PINK;
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>

        <Card className="p-5">
          <div className="flex flex-col gap-5 md:flex-row md:items-center">
            <div className="shrink-0">
              <input
                ref={profilePhotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) =>
                  handleProfilePhotoPick(e.target.files?.[0] || null)
                }
              />

              <button
                type="button"
                onClick={openProfilePhotoPicker}
                disabled={!isEditMode || saving}
                title={
                  isEditMode
                    ? "Click to change profile photo"
                    : "Click Edit Profile to change photo"
                }
                className={`relative flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-100 transition ${
                  isEditMode
                    ? "cursor-pointer border-[#f4a3ad] ring-4 ring-rose-100"
                    : "cursor-default"
                }`}
              >
                {visibleAvatarUrl ? (
                  <img
                    src={visibleAvatarUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-bold text-slate-500">
                    {String(user?.name || "A").charAt(0).toUpperCase()}
                  </span>
                )}

                {isEditMode ? (
                  <span
                    className="absolute inset-0 flex items-center justify-center text-center text-xs font-bold text-white"
                    style={{ backgroundColor: "rgba(15, 23, 42, 0.55)" }}
                  >
                    Change
                    <br />
                    Photo
                  </span>
                ) : null}
              </button>

              {isEditMode && profilePhotoFile ? (
                <div className="mt-2 w-28 truncate text-center text-xs font-medium text-slate-500">
                  {profilePhotoFile.name}
                </div>
              ) : null}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <h2 className="truncate text-2xl font-bold text-slate-800">
                    {user?.name || "-"}
                  </h2>
                  <p className="mt-1 break-words text-sm text-slate-500">
                    {user?.email || "-"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <StatusPill value={user?.role || "-"} type="info" />
                    <StatusPill value={user?.status || "-"} type="success" />
                    <StatusPill
                      value={user?.approvalStatus || "NOT_SUBMITTED"}
                      type={getApprovalTone(user?.approvalStatus)}
                    />
                    <StatusPill
                      value={
                        user?.isOnboardingComplete
                          ? "Onboarding Complete"
                          : `Onboarding Step ${user?.onboardingStep || 0}`
                      }
                      type={getOnboardingTone(user?.isOnboardingComplete)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:min-w-[280px]">
                  <Field label="Phone" value={user?.phone} />
                  <Field label="Department" value={user?.department} />
                  <Field label="Employee ID" value={user?.employeeId} />
                  <Field label="Last Login" value={formatDateTime(user?.lastLogin)} />
                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card className="p-5">
            <SectionTitle
              title="Personal Information"
              subtitle="Basic details visible on your profile."
            />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Full Name" value={user?.name} />
              <Field label="Email" value={user?.email} />
              <Field label="Phone" value={user?.phone} />
              <Field label="Bio" value={user?.bio} />
              <Field label="User Type" value={user?.userType} />
              <Field label="Verified" value={yesNo(user?.isVerified)} />
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle
              title="Work Information"
              subtitle="Role and account-level work details."
            />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Role" value={user?.role} />
              <Field label="Department" value={user?.department} />
              <Field label="Status" value={user?.status} />
              <Field label="Employee ID" value={user?.employeeId} />
              <Field label="Current Dashboard" value={user?.currentDashboard} />
              <Field
                label="Selected Warehouse"
                value={user?.selectedWarehouseName || user?.selectedWarehouseId}
              />
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <SectionTitle
            title="Onboarding & Approval"
            subtitle="Only the important onboarding and approval details."
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Onboarding Complete"
              value={yesNo(user?.isOnboardingComplete)}
            />
            <Field label="Onboarding Step" value={user?.onboardingStep} />
            <Field label="Approval Status" value={user?.approvalStatus} />
            {/* <Field label="Approved" value={yesNo(user?.isApproved)} /> */}
            {/* <Field label="Next Action" value={user?.nextAction} /> */}
            <Field label="Approval Note" value={user?.approvalNote} />
            <Field label="Must Change Password" value={yesNo(user?.mustChangePassword)} />
            <Field label="Password Set At" value={formatDateTime(user?.passwordSetAt)} />
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle
            title="Address"
            subtitle="Saved onboarding address information."
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Field label="Full Address" value={fullAddress} className="lg:col-span-3" />
            <Field label="Line 1" value={onboardingAddress?.line1} />
            <Field label="City" value={onboardingAddress?.city} />
            <Field label="State" value={onboardingAddress?.state} />
            <Field label="Country" value={onboardingAddress?.country} />
            <Field label="Pincode" value={onboardingAddress?.pincode} />
            <Field label="Notes" value={user?.onboarding?.notes} />
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle
            title="Emergency Contact"
            subtitle="Keep this updated so support and admin teams can rely on it."
          />

          {isEditMode ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextInput
                label="Name"
                value={emergencyName}
                onChange={(e) => setEmergencyName(e.target.value)}
                placeholder="Emergency contact name"
              />
              <TextInput
                label="Phone"
                value={emergencyPhone}
                onChange={(e) => setEmergencyPhone(e.target.value)}
                placeholder="Emergency contact phone"
              />
              <TextInput
                label="Relation"
                value={emergencyRelation}
                onChange={(e) => setEmergencyRelation(e.target.value)}
                placeholder="Relation"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="Name" value={emergencyName} />
              <Field label="Phone" value={emergencyPhone} />
              <Field label="Relation" value={emergencyRelation} />
            </div>
          )}
        </Card>

        <Card className="p-5">
          <SectionTitle
            title="Reference Contact"
            subtitle="Currently showing the first reference contact from onboarding."
          />

          {isEditMode ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextInput
                label="Name"
                value={referenceName}
                onChange={(e) => setReferenceName(e.target.value)}
                placeholder="Reference name"
              />
              <TextInput
                label="Phone"
                value={referencePhone}
                onChange={(e) => setReferencePhone(e.target.value)}
                placeholder="Reference phone"
              />
              <TextInput
                label="Relation"
                value={referenceRelation}
                onChange={(e) => setReferenceRelation(e.target.value)}
                placeholder="Relation"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="Name" value={referenceName} />
              <Field label="Phone" value={referencePhone} />
              <Field label="Relation" value={referenceRelation} />
            </div>
          )}
        </Card>

        <Card className="p-5">
          <SectionTitle
            title="Documents"
            subtitle="Uploaded onboarding documents."
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FileRow
              label="Profile Photo"
              file={profilePhotoFile}
              existingUrl={docs?.profilePhoto?.url}
              existingName={docs?.profilePhoto?.originalName}
              onChange={handleProfilePhotoPick}
              editable={isEditMode}
            />

            <FileRow
              label="Aadhaar"
              file={aadhaarFile}
              existingUrl={docs?.aadhaar?.url}
              existingName={docs?.aadhaar?.originalName}
              onChange={setAadhaarFile}
              editable={isEditMode}
            />

            <FileRow
              label="PAN"
              file={panFile}
              existingUrl={docs?.pan?.url}
              existingName={docs?.pan?.originalName}
              onChange={setPanFile}
              editable={isEditMode}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}